import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import DestinationBG from "../assets/images/destinationSelector.jpg";
import logo from "../assets/images/svg/WPC LOGO.svg";

function DestinationSelector() {
  const [selectedCountry, setSelectedCountry] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Safe localStorage
  const getLocalStorageItem = (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const setLocalStorageItem = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {}
  };

  useEffect(() => {
    if (getLocalStorageItem("destination")) {
      navigate("/home");
    }
  }, [navigate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (country) => {
    setSelectedCountry(country);
    setIsOpen(false);
  };

  const handleSubmit = () => {
    if (selectedCountry) {
      setLocalStorageItem("destination", selectedCountry.toLowerCase());
      window.dispatchEvent(new Event("storageUpdate"));
      navigate("/home");
    }
  };

  const getCountryCode = (countryName) => {
    const map = {
      Canada: "CA",
      Netherlands: "NL",
      "United Arab Emirates": "AE",
      "United Kingdom": "GB",
      "Czech Republic": "CZ",
      "Saudi Arabia": "SA",
      Sweden: "SE",
      Lithuania: "LT",
      Montenegro: "ME",
      Poland: "PL",
      Romania: "RO",
      Serbia: "RS",
      Ukraine: "UA",
    };
    return map[countryName] || "UN";
  };

  const whiteCollar = [
    { name: "Canada", emoji: "🇨🇦" },
    { name: "Netherlands", emoji: "🇳🇱" },
    { name: "Sweden", emoji: "🇸🇪" },
    { name: "Saudi Arabia", emoji: "🇸🇦" },
    { name: "United Kingdom", emoji: "🇬🇧" },
    { name: "United Arab Emirates", emoji: "🇦🇪" },
  ];

  const blueCollar = [
    { name: "Czech Republic", emoji: "🇨🇿" },
    { name: "Lithuania", emoji: "🇱🇹" },
    { name: "Montenegro", emoji: "🇲🇪" },
    { name: "Poland", emoji: "🇵🇱" },
    { name: "Romania", emoji: "🇷🇴" },
    { name: "Serbia", emoji: "🇷🇸" },
    { name: "Ukraine", emoji: "🇺🇦" },
  ];

  return (
    <div className="relative h-screen w-full overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${DestinationBG})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60 backdrop-blur-sm" />
      </div>

      <div className="relative z-10 flex h-full items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
          <div className="mb-8 text-center">
            <img src={logo} alt="logo" className="w-48 md:w-52 mx-auto" />
            <p className="text-gray-600 mt-4 text-center">
              Streamline your work permit journey with expert guidance and a
              hassle-free process.
            </p>
            <div className="flex items-center justify-center gap-2">
              <div className="h-px w-8 bg-gradient-to-r from-transparent to-blue-500"></div>
              <p className="text-xs font-medium text-myBlue tracking-wide uppercase">
                Your Global Career Partner
              </p>
              <div className="h-px w-8 bg-gradient-to-l from-transparent to-blue-500"></div>
            </div>
          </div>

          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2 ml-1">
              Select Your Destination
            </label>

            {/* Custom Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer text-left flex items-center justify-between hover:border-gray-300 transition-colors"
              >
                <span className="text-gray-600">
                  {selectedCountry ? (
                    <>
                      <span className="text-myBlue">
                        {whiteCollar.find((c) => c.name === selectedCountry)
                          ?.emoji ||
                          blueCollar.find((c) => c.name === selectedCountry)
                            ?.emoji}{" "}
                      </span>
                      {selectedCountry}
                    </>
                  ) : (
                    "Choose your dream destination"
                  )}
                </span>
                <span
                  className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                >
                  ▼
                </span>
              </button>

              {/* Dropdown Menu - Opens Downward with Scroll */}
              {isOpen && (
                <div className="absolute z-50 w-full mt-2 bg-white rounded-2xl border-2 border-gray-200 shadow-xl overflow-hidden">
                  <div
                    className="max-h-[280px] overflow-y-auto py-2 scroll-smooth custom-scrollbar"
                    style={{
                      scrollbarWidth: "thin",
                      scrollbarColor: "#bfbfbf transparent",
                    }}
                  >
                    {/* White Collar Group */}
                    <div className=" flex justify-center px-4 py-2 text-myBlue font-semibold text-sm border-b border-gray-100 bg-gray-50">
                      ━━━ Highly Skilled Migrants ━━━
                    </div>
                    {whiteCollar.map((c, i) => (
                      <div
                        key={i}
                        onClick={() => handleSelect(c.name)}
                        className="px-4 py-1 hover:bg-blue-50 cursor-pointer flex items-center gap-3 transition-colors text-gray-700 border-b border-gray-100 last:border-0"
                      >
                        <span className="text-xl text-myBlue">{c.emoji}</span>
                        <span>{c.name}</span>
                      </div>
                    ))}

                    {/* Blue Collar Group */}
                    <div className="flex justify-center px-4 py-2 text-myBlue font-semibold text-sm border-b border-gray-100 bg-gray-50 mt-2">
                      ━━━ Skilled Trade & Workforce ━━━
                    </div>
                    {blueCollar.map((c, i) => (
                      <div
                        key={i}
                        onClick={() => handleSelect(c.name)}
                        className="px-4 py-1 hover:bg-blue-50 cursor-pointer flex items-center gap-3 transition-colors text-gray-700 border-b border-gray-100 last:border-0"
                      >
                        <span className="text-xl text-myBlue">{c.emoji}</span>
                        <span>{c.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Real Flag Display After Selection */}
            {selectedCountry && (
              <div className="mt-5 p-4 bg-blue-50 rounded-2xl flex items-center gap-4 border border-blue-100">
                <ReactCountryFlag
                  countryCode={getCountryCode(selectedCountry)}
                  svg
                  style={{ width: "48px", height: "34px", borderRadius: "6px" }}
                />
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-500">
                    Selected Destination
                  </p>
                  <p className="text-2xl font-semibold text-myBlue">
                    {selectedCountry}
                  </p>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleSubmit}
            disabled={!selectedCountry}
            className={`w-full py-4 rounded-2xl font-semibold text-lg transition-all ${
              selectedCountry
                ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:brightness-105"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {selectedCountry
              ? `Continue with ${selectedCountry}`
              : "Select a Destination to Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DestinationSelector;
